import { TestMarke } from "@/components/TestMarke";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ClipboardCheck, FileText, Plus, Receipt, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { aktion as ablaufAktion } from "@/lib/ablauf";
import { stationenBerechnen, StationenLeiste } from "@/lib/vorgangStationen";

/**
 * ProjektVorgang – Kopf des geöffneten Projekts (A-015b, 17.10.2026; ersetzt den Reiter „Belege“ aus A-015).
 *   • Leiste „+ Angebot · + Auftragsbestätigung · + Rechnung“ für genau dieses Projekt
 *     (Kunde + Projekt fest, Betreff = Projekttitel); am Handy Auswahlliste statt Knopfwand.
 *   • Vorgangskette Angebot → AB → Rechnung mit Nummer, Status, Betrag (Klick öffnet).
 *   • Stationen-Leiste wie in „Dokumente & Vorgänge“.
 *   • Der nächste sinnvolle Schritt ist hervorgehoben (über die bestehenden Umwandeln-Wege).
 */
const ART = {
  quote: { label: "Angebot", kurz: "Angebot", Icon: FileText, farbe: "text-blue-600", rand: "border-blue-200", url: (id) => `/quotes/edit/${id}` },
  order: { label: "Auftragsbestätigung", kurz: "AB", Icon: ClipboardCheck, farbe: "text-purple-600", rand: "border-purple-200", url: (id) => `/orders/edit/${id}` },
  invoice: { label: "Rechnung", kurz: "Rechnung", Icon: Receipt, farbe: "text-green-600", rand: "border-green-200", url: (id) => `/invoices/edit/${id}` },
};
const fmtEur = (n) => (Number(n) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const nummer = (d) => d.quote_number || d.order_number || d.invoice_number || d.nummer || (d.invoice_number === "" ? "Entwurf" : "");  // A-023: Rechnung ohne Nummer
const datum = (d) => d.created_at || d.date || "";
const nachDatum = (a, b) => String(datum(a)).localeCompare(String(datum(b)));
const versendet = (d, art) => !!d.versendet_am || !(art === "quote" ? ["Entwurf", "", undefined] : ["Entwurf", "Offen", "", undefined]).includes(d.status);

/** Belege des Projekts als Ketten: Angebot → AB(s) → Rechnung(en); lose ABs/Rechnungen als eigene Kette. */
export const kettenBilden = ({ quotes, orders, invoices }) => {
  const benutzt = new Set();
  const rechnungenZu = (feld, id) => invoices.filter((i) => i[feld] === id && !benutzt.has(i.id));
  const abKette = (o) => {
    benutzt.add(o.id);
    const r = rechnungenZu("order_id", o.id); r.forEach((i) => benutzt.add(i.id));
    return [{ art: "order", d: o }, ...r.sort(nachDatum).map((i) => ({ art: "invoice", d: i }))];
  };
  const ketten = [];
  [...quotes].sort(nachDatum).forEach((q) => {
    const glieder = [{ art: "quote", d: q }];
    orders.filter((o) => o.quote_id === q.id).sort(nachDatum).forEach((o) => glieder.push(...abKette(o)));
    const direkt = rechnungenZu("quote_id", q.id).filter((i) => !i.order_id); direkt.forEach((i) => benutzt.add(i.id));
    direkt.sort(nachDatum).forEach((i) => glieder.push({ art: "invoice", d: i }));
    ketten.push(glieder);
  });
  orders.filter((o) => !benutzt.has(o.id)).sort(nachDatum).forEach((o) => ketten.push(abKette(o)));
  invoices.filter((i) => !benutzt.has(i.id)).sort(nachDatum).forEach((i) => ketten.push([{ art: "invoice", d: i }]));
  return ketten;
};

/** Nächster sinnvoller Schritt im Vorgang (nur einer, damit die Suite führt). */
export const naechsterSchritt = ({ quotes, orders, invoices }) => {
  const letzte = (l) => [...l].sort(nachDatum).at(-1);
  const rechnungen = invoices.filter((i) => i.art !== "anzahlung" && i.status !== "Storniert");
  const r = letzte(rechnungen);
  if (r) {
    if (r.status === "Bezahlt") return { art: "fertig", text: "Vorgang abgeschlossen: Rechnung bezahlt." };
    if (!versendet(r, "invoice")) return { art: "oeffnen", d: r, typ: "invoice", text: `Rechnung ${nummer(r)} prüfen und senden` };
    return { art: "oeffnen", d: r, typ: "invoice", text: `Zahlung zu Rechnung ${nummer(r)} prüfen` };
  }
  const o = letzte(orders);
  if (o) {
    if (!versendet(o, "order")) return { art: "oeffnen", d: o, typ: "order", text: `Auftragsbestätigung ${nummer(o)} prüfen und senden` };
    return { art: "rechnung-aus-ab", d: o, text: `Rechnung aus Auftragsbestätigung ${nummer(o)} erstellen` };
  }
  const q = letzte(quotes.filter((x) => x.status !== "Abgelehnt"));
  if (q) {
    if (!versendet(q, "quote")) return { art: "oeffnen", d: q, typ: "quote", text: `Angebot ${nummer(q)} fertigstellen und senden` };
    return { art: "ab-aus-angebot", d: q, text: `Auftragsbestätigung aus Angebot ${nummer(q)} erstellen` };
  }
  return { art: "neu-quote", text: "Angebot erstellen" };
};

const ProjektBelege = ({ projektId, kundeId, kunde = null }) => {
  const navigate = useNavigate();
  const [docs, setDocs] = useState(null);
  const [termine, setTermine] = useState([]);
  const [neu, setNeu] = useState("");
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    let lebt = true;
    const holen = (pfad) => api.get(pfad).then((r) => (r.data || []).filter((x) => x.customer_id === kundeId && x.projekt_id === projektId)).catch(() => []);
    Promise.all([holen("/quotes"), holen("/orders"), holen("/invoices"),
      api.get(`/module-projekte/${projektId}/belege`).then((r) => (r.data || []).filter((b) => b.v2)).catch(() => [])])
      .then(([quotes, orders, invoices, v2]) => { if (lebt) setDocs({ quotes, orders, invoices, v2 }); });
    api.get("/module-termine", { params: { kunde_id: kundeId } })
      .then((r) => { if (lebt) setTermine((Array.isArray(r.data) ? r.data : []).filter((t) => !t.projekt_id || t.projekt_id === projektId)); }).catch(() => {});
    return () => { lebt = false; };
  }, [projektId, kundeId]);

  const anlegen = async (art) => {
    setNeu("");
    if (!art) return;
    ablaufAktion(`${ART[art].label} erstellen`, { projekt_id: projektId, kunde_id: kundeId });
    if (art === "quote") { navigate(`/quotes/new?customer=${kundeId}&projekt_id=${projektId}`); return; }
    if (art === "invoice") { navigate(`/invoices/new?customer=${kundeId}&projekt_id=${projektId}`); return; }
    try {
      const res = await api.post(`/orders/blank-for-customer/${kundeId}`, { projekt_id: projektId });
      toast.success("Auftragsbestätigung angelegt");
      navigate(`/orders/edit/${res.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler beim Anlegen"); }
  };

  const oeffnen = (d, art) => {
    if (d.v2) { navigate("/rechnungen-v2"); return; }
    if (versendet(d, art)) {
      const label = art === "quote" ? "Dieses Angebot" : art === "order" ? "Diese Auftragsbestätigung" : "Diese Rechnung";
      if (!window.confirm(`${label} wurde bereits versendet oder bearbeitet (Status: ${d.status}). Möchten Sie es wirklich bearbeiten? Ein erneutes Bearbeiten kann dazu führen, dass eine veränderte Version erneut verschickt wird.`)) return;
    }
    ablaufAktion("Dokument öffnen", { dokument_id: d.id, dokument_art: art, kunde_id: kundeId, projekt_id: projektId });
    navigate(ART[art].url(d.id));
  };

  // Bestehende Umwandeln-Wege (wie in „Dokumente & Vorgänge“)
  const schrittAusfuehren = async (s) => {
    if (s.art === "neu-quote") { anlegen("quote"); return; }
    if (s.art === "oeffnen") { oeffnen(s.d, s.typ); return; }
    setLaeuft(true);
    try {
      if (s.art === "ab-aus-angebot") {
        const res = await api.post(`/orders/from-quote/${s.d.id}`);
        toast.success("Auftragsbestätigung erstellt");
        navigate(`/orders/edit/${res.data.id}`);
      } else if (s.art === "rechnung-aus-ab") {
        const res = await api.post(`/invoices/from-order/${s.d.id}`);
        await api.post(`/invoices/${res.data.id}/anzahlungen-abziehen`)
          .catch(() => toast.error("Anzahlungen konnten nicht abgezogen werden – bitte Rechnung prüfen"));
        toast.success("Rechnung erstellt");
        navigate(`/invoices/edit/${res.data.id}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); } finally { setLaeuft(false); }
  };

  if (!docs) return <div className="text-xs text-muted-foreground" data-testid={`projekt-vorgang-${projektId}`}>Belege werden geladen …</div>;

  const ketten = kettenBilden(docs);
  const schritt = naechsterSchritt(docs);
  const letzteAB = [...docs.orders].sort(nachDatum).at(-1) || null;
  const stationen = stationenBerechnen({ kunde, termine, quotes: docs.quotes, invoices: docs.invoices, order: letzteAB });
  const anzahl = docs.quotes.length + docs.orders.length + docs.invoices.length + docs.v2.length;
  // „+ Angebot“ ist selbst der nächste Schritt → dann hervorgehoben, sonst alle drei dezent
  const neuKnopf = (art) => {
    const haupt = schritt.art === "neu-quote" && art === "quote";
    return (
      <button key={art} type="button" onClick={() => anlegen(art)} data-testid={`projekt-neu-${art}-${projektId}`}
        className={`inline-flex items-center gap-1 h-8 px-2.5 rounded-sm text-xs font-medium border transition-colors ${haupt ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90" : `bg-background hover:bg-muted ${ART[art].rand} ${ART[art].farbe}`}`}>
        <Plus className="w-3.5 h-3.5" /> {ART[art].label}
      </button>
    );
  };

  return (
    <div className="space-y-2.5" data-testid={`projekt-vorgang-${projektId}`}>
      {/* Leiste: neuer Beleg für genau dieses Projekt. Desktop drei Knöpfe, Handy Auswahlliste. */}
      <div className="flex flex-wrap items-center gap-2" data-testid={`projekt-belegleiste-${projektId}`}>
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Belege zu diesem Projekt</span>
        <div className="hidden sm:flex flex-wrap items-center gap-1.5 sm:ml-auto">
          {neuKnopf("quote")}{neuKnopf("order")}{neuKnopf("invoice")}
        </div>
        <select value={neu} onChange={(e) => anlegen(e.target.value)} aria-label="Neuer Beleg zu diesem Projekt"
          className="sm:hidden w-full h-10 rounded-sm border border-input bg-background px-3 text-sm" data-testid={`projekt-beleg-neu-${projektId}`}>
          <option value="">+ Neuer Beleg zu diesem Projekt …</option>
          <option value="quote">Angebot</option>
          <option value="order">Auftragsbestätigung</option>
          <option value="invoice">Rechnung</option>
        </select>
      </div>

      {/* Nächster Schritt (hervorgehoben) */}
      {schritt.art === "fertig" ? (
        <div className="text-xs text-green-800 bg-green-50 border border-green-200 rounded-sm px-2.5 py-1.5" data-testid={`projekt-naechster-schritt-${projektId}`} data-art="fertig">✓ {schritt.text}</div>
      ) : schritt.art !== "neu-quote" && (
        <button type="button" onClick={() => schrittAusfuehren(schritt)} disabled={laeuft}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 min-h-[2.25rem] px-3 py-1.5 rounded-sm text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 text-left"
          data-testid={`projekt-naechster-schritt-${projektId}`} data-art={schritt.art}>
          <Sparkles className="w-4 h-4 shrink-0" /> <span>Nächster Schritt: {schritt.text}</span>
        </button>
      )}

      {anzahl === 0 ? (
        <div className="text-xs text-muted-foreground border-2 border-dashed rounded p-3 text-center" data-testid={`projekt-belege-leer-${projektId}`}>
          Noch keine Belege zu diesem Projekt. Ältere Belege ohne Projekt stehen links unter „Dokumente &amp; Vorgänge“ (Kennzeichen „ohne Projekt – zuordnen“).
        </div>
      ) : (
        <>
          <StationenLeiste stationen={stationen} testId={`projekt-stationen-${projektId}`} idSuffix={projektId} />
          <div className="space-y-1.5" data-testid={`projekt-kette-${projektId}`}>
            {ketten.map((kette) => (
              <div key={kette[0].d.id} className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1" data-testid={`projekt-kette-zeile-${kette[0].d.id}`}>
                {kette.map((g, i) => {
                  const a = ART[g.art];
                  const anz = g.d.art === "anzahlung";
                  return (
                    <span key={g.d.id} className="flex items-center gap-1 min-w-0">
                      {i > 0 && <ArrowRight className="w-3.5 h-3.5 text-slate-400 shrink-0 max-sm:rotate-90 max-sm:ml-3" />}
                      <button type="button" onClick={() => oeffnen(g.d, g.art)} data-testid={`projekt-beleg-${g.d.id}`}
                        title={`${anz ? "Anzahlungsrechnung" : a.label} ${nummer(g.d)} öffnen`}
                        className={`min-w-0 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs border rounded-sm px-2 py-1 bg-background hover:bg-muted/50 ${a.rand}`}>
                        <a.Icon className={`w-3.5 h-3.5 shrink-0 ${a.farbe}`} />
                        <span className="font-medium">{anz ? "Anzahlung" : a.kurz}</span>
                        <span className="font-mono">{nummer(g.d)}</span>{g.d.uebungsbeleg && <TestMarke className="ml-1" />}
                        <span className="px-1 rounded-sm bg-slate-100 text-slate-700">{g.d.status || "—"}</span>
                        <span className="font-mono">{fmtEur(g.d.total_gross ?? g.d.brutto ?? 0)}</span>
                      </button>
                    </span>
                  );
                })}
              </div>
            ))}
            {docs.v2.map((b) => (
              <button key={b.id} type="button" onClick={() => navigate("/rechnungen-v2")} data-testid={`projekt-beleg-${b.id}`}
                className="flex flex-wrap items-center gap-x-1.5 text-xs border rounded-sm px-2 py-1 bg-background hover:bg-muted/50 border-green-200">
                <Receipt className="w-3.5 h-3.5 text-green-600" /> <span className="font-medium">Rechnung (V2)</span>
                <span className="font-mono">{b.nummer}</span><span className="px-1 rounded-sm bg-slate-100 text-slate-700">{b.status || "—"}</span>
                <span className="font-mono">{fmtEur(b.betrag)}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ProjektBelege;
